import React from "react";
import { useField, useFormikContext } from "formik";
import { FormRadioFieldConfig } from "../types";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import Radio from "shared/form/core/radio";
import { RadioOptionsDisplayStyle } from "shared/form/enum";
import classNames from "classnames";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { ValidateSingleSelectedFormOption } from "../utils/resetOptionsForInvalidSelection";

/**
 * @description render a field with radio options within a form. Default style will be vertical
 */
function FormRadioField({ fullWidth = true, onChange, ...props }: FormRadioFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, handleChange } = useFormikContext();
  const [{ ...field }, meta] = useField({ name: props.name, validate: props.validate });

  const changeHandler = React.useCallback(
    async e => {
      await handleChange(e);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
      onChange?.(e);
    },
    [handleChange, setFieldTouched, props.name, meta.touched, onChange]
  );

  const getConflictDisplayValue = React.useCallback(
    ({ value }) => {
      const optionObj = props.options.find(item => item.value === value);
      return optionObj ? optionObj.label : value;
    },
    [props.options]
  );

  const getDisplayValue = React.useCallback(() => {
    return props.options.find(item => item.value === field.value);
  }, [field.value, props.options]);

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={fullWidth} indented={props.indented}>
        <FormLabel
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.RADIO, value: getDisplayValue()?.label })
        ) : (
          <div
            className={classNames("radioBtnGroup", {
              "radioBtnGroup--horizontal": props.displayStyle === RadioOptionsDisplayStyle.HORIZONTAL,
              "radioBtnGroup--vertical": props.displayStyle === RadioOptionsDisplayStyle.VERTICAL
            })}>
            {props.options.map(option => (
              <Radio
                key={option.value}
                label={option.label}
                value={option.value}
                iconUrl={option.imageIcon?.url}
                name={props.name}
                checked={field.value === option.value}
                onChange={changeHandler}
                onBlur={field.onBlur}
                disabled={props.disabled}
              />
            ))}
          </div>
        )}
        <ValidateSingleSelectedFormOption
          options={props.options}
          value={field.value}
          name={props.name}
          fixedInitialValueOnReset={props.fixedInitialValueOnReset}
        />
        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField
            forFieldName={props.name}
            disabled={props.disabled}
            customDisplayValue={getConflictDisplayValue}
          />
        )}
        {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormRadioField;
